import React, { useState } from 'react'
import toast from 'react-hot-toast'

interface User {
  id: string
  email: string
  username?: string
  isAdmin: boolean
  createdAt: string
  subscriptionStatus?: string
  credits: number
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([
    {
      id: '1',
      email: 'admin@ladolcevita.com',
      username: 'admin',
      isAdmin: true,
      createdAt: '2024-01-01T00:00:00Z',
      subscriptionStatus: 'active',
      credits: 100
    },
    {
      id: '2',
      email: 'user@example.com',
      username: 'user1',
      isAdmin: false,
      createdAt: '2024-01-15T00:00:00Z',
      subscriptionStatus: 'active',
      credits: 50
    },
    {
      id: '3',
      email: 'test@example.com',
      username: 'testuser',
      isAdmin: false,
      createdAt: '2024-02-01T00:00:00Z',
      subscriptionStatus: 'inactive',
      credits: 3
    }
  ])

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const deleteUser = (userId: string) => {
    setUsers(users.filter(user => user.id !== userId))
    toast.success('Utente eliminato')
  }

  const toggleAdmin = (userId: string) => {
    setUsers(users.map(user =>
      user.id === userId ? { ...user, isAdmin: !user.isAdmin } : user
    ))
    toast.success('Privilegi admin aggiornati')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT')
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Gestione Utenti
            </h3>
            <div className="flex space-x-3">
              <input
                type="text"
                placeholder="Cerca utenti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
              <button className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
                Esporta
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Crediti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Registrazione
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium">
                              {user.username?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.username || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.isAdmin
                            ? 'bg-purple-100 text-purple-800'
                            : user.subscriptionStatus === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.isAdmin ? 'Admin' : user.subscriptionStatus === 'active' ? 'Attivo' : 'Inattivo'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.credits}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleAdmin(user.id)}
                          className={`px-3 py-1 rounded text-xs ${
                            user.isAdmin
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          }`}
                        >
                          {user.isAdmin ? 'Rimuovi Admin' : 'Rendi Admin'}
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="px-3 py-1 rounded text-xs bg-red-100 text-red-800 hover:bg-red-200"
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 text-sm text-gray-500">
            Totale utenti: {filteredUsers.length}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminUsersPage 